import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { LOGO_URL } from "../../data/landing";
import { useAuth } from "../../lib/auth";

// Import reusable footer parts
import FooterColumn from "./footer/FooterColumn";
import FooterLink from "./footer/FooterLink";
import SocialLinks from "./footer/SocialLinks";
import Newsletter from "./footer/Newsletter";
import FooterBottom from "./footer/FooterBottom";

export default function Footer({ isWorkspace = false }) {
  const { user } = useAuth();
  const isLoggedIn = Boolean(user || localStorage.getItem("studlyf_session_token"));
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem("sidebarCollapsed") === "true";
  });

  useEffect(() => {
    const handleToggle = (e) => {
      setSidebarCollapsed(e.detail);
    };
    window.addEventListener("sidebar-toggle", handleToggle);
    return () => window.removeEventListener("sidebar-toggle", handleToggle);
  }, []);

  const handleGetStartedClick = () => {
    const navBtn = document.querySelector('[data-testid="nav-get-started"]');
    if (navBtn) {
      navBtn.click();
    } else {
      window.location.href = "/workspace";
    }
  };

  // Determine container margin based on workspace shell layout
  const marginClass = isWorkspace
    ? sidebarCollapsed
      ? "lg:ml-[72px] lg:w-[calc(100%-72px)] transition-all duration-300"
      : "lg:ml-[260px] lg:w-[calc(100%-260px)] transition-all duration-300"
    : "w-full transition-all duration-300";

  return (
    <footer className={`w-full max-w-full bg-[#FAFAFC] border-t border-black/[0.06] rounded-t-[32px] md:rounded-t-[48px] pt-16 md:pt-20 pb-10 mt-20 md:mt-24 lg:mt-28 relative overflow-hidden z-0 ${marginClass}`}>
      {/* Background soft ambient blooms */}
      <div className="absolute top-0 left-1/4 w-[350px] h-[350px] rounded-full bg-gradient-to-tr from-[#7C3AED]/5 to-transparent blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[300px] h-[300px] rounded-full bg-gradient-to-bl from-[#EC4899]/3 to-transparent blur-3xl pointer-events-none" />

      <div className="max-w-[1600px] w-full mx-auto px-4 sm:px-6 md:px-10 lg:px-12">
        {/* Main Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-12 gap-8 md:gap-8 lg:gap-10 xl:gap-12">
          
          {/* Column 1: Logo & Brand Info */}
          <div className="flex flex-col gap-5 sm:col-span-2 md:col-span-4 lg:col-span-4">
            <Link to="/" className="flex items-center gap-2.5 w-fit" data-testid="footer-logo">
              <img src={LOGO_URL} alt="STUDLYF" className="h-10 sm:h-12 md:h-14 lg:h-16 w-auto max-w-[240px] object-contain" />
            </Link>
            
            <p className="text-sm text-gray-500 leading-relaxed max-w-sm">
              STUDLYF AI is an AI-powered startup operating system that helps founders build, validate, launch, market, fund, and scale their businesses—all from one intelligent workspace.
            </p>

            <div className="flex flex-wrap gap-3 mt-2">
              <button
                onClick={handleGetStartedClick}
                className="glow-button rounded-full px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-1.5 justify-center"
                data-testid="footer-cta-get-started"
              >
                <Sparkles className="w-4 h-4" /> Get Started
              </button>
              
              <Link
                to="/contact"
                className="glass rounded-full px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-1.5 justify-center border border-gray-200"
                data-testid="footer-cta-book-demo"
              >
                Book Demo
              </Link>
            </div>
          </div>

          {/* Column 2: Product */}
          <FooterColumn title="Product" className="col-span-1 md:col-span-2 lg:col-span-2">
            <FooterLink to="/workspace" label="AI Workspace" />
            <FooterLink to="/workspace/dashboard" label="BI Dashboard" />
            <FooterLink to="/workspace/projects" label="Projects" />
            <FooterLink to="/workspace/strategy-marketing" label="Strategy & Marketing" />
            <FooterLink to="/workspace/funding" label="Funding" />
            <FooterLink to="/templates" label="Templates" />
          </FooterColumn>

          {/* Column 3: Resources */}
          <FooterColumn title="Resources" className="col-span-1 md:col-span-2 lg:col-span-2">
            <FooterLink to="/discover" label="Discover" />
            <FooterLink to="/discover/startups" label="Startup Directory" />
            <FooterLink to="/discover/incubators" label="Incubators" />
            <FooterLink to="/discover/vc-firms" label="VC Firms" />
            <FooterLink to="/discover/blog" label="Startup Blog" />
            <FooterLink to="/discover/featured" label="Featured Lists" />
            <FooterLink to="/discover/hiring" label="Hiring Companies" />
            <FooterLink to="/help-center" label="Help Center" isPlaceholder={true} />
          </FooterColumn>

          {/* Column 4: Company */}
          <FooterColumn title="Company" className="col-span-1 md:col-span-2 lg:col-span-2">
            <FooterLink to="/about" label="About Us" />
            <FooterLink to="/#pricing" label="Pricing" />
            <FooterLink to="/contact" label="Contact" />
            <FooterLink to="/careers" label="Careers" isPlaceholder={true} />
            <FooterLink to="/privacy-policy" label="Privacy Policy" />
            <FooterLink to="/terms-and-conditions" label="Terms & Conditions" />
            <FooterLink to="/cookie-policy" label="Cookie Policy" isPlaceholder={true} />
          </FooterColumn>

          {/* Column 5: Connect */}
          <FooterColumn title="Connect" className="col-span-1 md:col-span-2 lg:col-span-2">
            <p className="text-sm text-gray-500 mb-1 leading-relaxed">
              Join our startup community and get direct support.
            </p>
            <SocialLinks />
          </FooterColumn>

        </div>

        {/* Newsletter Section */}
        <Newsletter />

        {/* Bottom Bar Section */}
        <FooterBottom />
      </div>
    </footer>
  );
}
